
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ModeToggle } from "@/components/ModeToggle";
import { useFeedbackData } from './feedback-view/useFeedbackData';
import { CandidateFeedbackView } from './feedback-view/CandidateFeedbackView';
import { RecruiterFeedbackView } from './feedback-view/RecruiterFeedbackView';
import { AccessDenied } from './feedback-view/AccessDenied';
import CreateAccountPrompt from './feedback-view/CreateAccountPrompt';
import { Folder, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from './feedback-view/LoadingSpinner';

interface ProjectInfo {
  id: string;
  name: string;
}

interface FeedbackViewProps {
  allowAccess?: boolean;
}

const FeedbackView: React.FC<FeedbackViewProps> = ({ allowAccess: externalAllowAccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { 
    feedback, 
    loading, 
    hasAccess, 
    requiredLogin, 
    formatDate, 
    isCandidateFeedback, 
    canViewFeedback,
    showCreateAccountPrompt,
    error
  } = useFeedbackData();
  
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [checkingProject, setCheckingProject] = useState(false);
  
  console.log("FeedbackView rendering with:", {
    feedback: feedback?.id,
    loading,
    hasAccess,
    canViewFeedback,
    externalAllowAccess,
    locationState: location.state
  });
  
  // Use external access control if provided, otherwise use the hook's access control
  const effectiveAccessControl = externalAllowAccess !== undefined ? externalAllowAccess : canViewFeedback;

  // Vérifier si le feedback est associé à un projet
  useEffect(() => {
    if (!feedback?.id || !user) return;
    
    const checkProjectAssociation = async () => {
      setCheckingProject(true);
      try {
        // Log the feedback ID to make sure we're using the correct one
        console.log("Checking project association for feedback ID:", feedback.id);
        
        const { data, error } = await supabase
          .from('projects_feedbacks')
          .select(`
            project_id,
            projects (id, name)
          `)
          .eq('feedback_id', feedback.id)
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) {
          console.error('Error checking project association:', error);
          return;
        }
        
        if (data && data.project_id && data.projects) {
          setProjectInfo({
            id: data.project_id,
            name: data.projects.name
          });
          console.log("Found project info:", data.projects);
        } else {
          console.log("No project association found for feedback:", feedback.id);
        }
      } catch (error) {
        console.error('Error in checkProjectAssociation:', error);
      } finally {
        setCheckingProject(false);
      }
    };
    
    checkProjectAssociation();
  }, [feedback?.id, user]);

  const handleViewProject = () => {
    if (projectInfo) {
      // Log to verify the correct project ID before navigation
      console.log("Navigating to project:", projectInfo.id);
      navigate(`/projects/${projectInfo.id}`);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="flex justify-center py-12">
        <AccessDenied 
          message="Erreur" 
          description={error} 
          onReturn={() => navigate('/dashboard')} 
        />
      </div>
    );
  }

  if (!effectiveAccessControl) {
    return (
      <div className="flex justify-center py-12">
        <AccessDenied 
          message="Accès refusé" 
          description={requiredLogin 
            ? "Vous devez vous connecter pour voir ce feedback." 
            : "Vous n'avez pas la permission de voir ce feedback."} 
          onReturn={() => navigate('/dashboard')} 
        />
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="flex justify-center py-12">
        <AccessDenied 
          message="Feedback non trouvé" 
          description="Le feedback demandé n'existe pas ou a été supprimé." 
          onReturn={() => navigate('/dashboard')} 
        />
      </div>
    );
  }

  // Show create account prompt if applicable
  if (showCreateAccountPrompt) {
    return (
      <div className="container max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <CreateAccountPrompt 
            feedbackId={feedback?.id || ''} 
            candidateEmail={feedback?.candidate_email || undefined} 
          />
        </div>
        {isCandidateFeedback ? (
          <CandidateFeedbackView feedback={feedback} formatDate={formatDate} navigate={navigate} />
        ) : (
          <RecruiterFeedbackView feedback={feedback} formatDate={formatDate} navigate={navigate} />
        )}
      </div>
    );
  }

  return (
    <div className="container max-w-3xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge>{isCandidateFeedback ? 'Feedback Candidat' : 'Feedback Recruteur'}</Badge>
          
          {projectInfo && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleViewProject}
              className="flex items-center gap-1"
            >
              <Folder className="h-4 w-4 mr-1" />
              <span>Projet: {projectInfo.name}</span>
            </Button>
          )}
        </div>
        <ModeToggle />
      </div>
      
      {isCandidateFeedback ? (
        <CandidateFeedbackView feedback={feedback} formatDate={formatDate} navigate={navigate} />
      ) : (
        <RecruiterFeedbackView feedback={feedback} formatDate={formatDate} navigate={navigate} />
      )}
    </div>
  );
};

export default FeedbackView;
